import { useState, useRef, useCallback } from "react";

interface UseSpeechRecognitionReturn {
  isListening: boolean;
  transcript: string;
  startListening: () => void;
  stopListening: () => void;
  isSupported: boolean;
  error: string | null;
}

/**
 * useSpeechRecognition — 纯浏览器 Web Speech API 方案。
 *
 * 参考 speakandrecord.com 的实现，不依赖任何第三方库/Capacitor 插件。
 * 所有平台(含 Android Chrome)都走 webkitSpeechRecognition。
 *
 * 核心逻辑:
 *   1. getUserMedia({audio:true}) 触发浏览器原生麦克风权限弹窗
 *   2. webkitSpeechRecognition 持续语音识别(continuous=true)
 *   3. onend 时自动续听，直到外部调用 stopListening()
 */
const SpeechRecAPI =
  typeof window !== "undefined"
    ? (window as unknown as Record<string, unknown>).SpeechRecognition ||
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition
    : null;

export function useSpeechRecognition(): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSupported] = useState(() => !!SpeechRecAPI);

  // Track whether we intend to keep listening (for auto-restart in onend)
  const listeningRef = useRef(false);
  const recognitionRef = useRef<InstanceType<typeof SpeechRecAPI> | null>(null);

  const stopListening = useCallback(() => {
    listeningRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecAPI) {
      setError("当前浏览器不支持语音识别，请使用 Chrome");
      return;
    }
    setError(null);
    setTranscript("");
    listeningRef.current = true;

    try {
      const recognition = new SpeechRecAPI();
      recognition.lang = "zh-CN";
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalT = "";
        let interimT = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (r.isFinal) finalT += r[0].transcript;
          else interimT += r[0].transcript;
        }
        setTranscript(finalT || interimT);
      };

      recognition.onerror = (event: Event) => {
        const err = event as SpeechRecognitionErrorEvent;
        if (err.error === "not-allowed") {
          setError("请在浏览器设置中允许麦克风权限");
        } else if (err.error === "no-speech") {
          setError("未检测到语音，请再试一次");
        } else if (err.error !== "aborted") {
          // 其他错误不覆盖已有错误提示
        }
        // 不自动 set isListening=false — onend 会处理
      };

      recognition.onend = () => {
        // 如果仍然在监听状态，自动续听（类似 speakandrecord 的行为）
        if (listeningRef.current) {
          try { recognition.start(); } catch { /* might already be started */ }
          return;
        }
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognitionRef.current = recognition;
      recognition.start();
      setIsListening(true);
    } catch {
      setError("语音启动失败，请检查麦克风权限");
    }
  }, []);

  return { isListening, transcript, startListening, stopListening, isSupported, error };
}
